import { LitElement, html, css } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { arrowDownIcon, checkIcon } from '../icons/icons';
import { sharedStyles } from '../shared-styles';

@customElement('dropdown-multiselect-element')
export class DropdownMultiselectElement extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        max-height: 66px;
      }

      .wrapper:hover {
        cursor: pointer;
      }

      .wrapper {
        border: 2px solid var(--dark-background-color);
        border-radius: 5px;

        position: relative;
      }

      .selected-display {
        padding: 5px;
        min-width: 200px;
        max-width: 200px;
        min-height: 30px;
      }

      .selected-text-display {
        overflow: hidden;
        text-overflow: ellipsis;
        max-height: 62px;
      }

      .row {
        display: flex;
        flex-direction: row;
        width: 100%;
        padding-top: 4px;
        padding-bottom: 4px;
      }

      .selectable-row:hover {
        background-color: var(--background-color-hover);
      }

      .selectables {
        position: absolute;
        background-color: white;
        display: none;
        width: 100%;
        border: 1px solid var(--dark-background-color);
        border-radius: 4px;
        z-index: 9999;
      }

      :host([selectablesVisible]) .selectables {
        display: block;
      }

      .empty {
        width: 24px;
      }

      .down-arrow {
        width: 24px;
      }
    `,
  ];

  @property({ type: Array, attribute: true })
  public selectable: Array<string> = [];

  @property({ type: Array, attribute: true, reflect: true })
  public selected: Array<string> = [];

  @property({ type: Boolean, reflect: true })
  public selectablesVisible = false;

  constructor() {
    super();

    document.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._handleClick);
  }

  _handleClick = (e: Event) => {
    if (!e.composedPath().includes(this)) {
      this.selectablesVisible = false;
    }
  };

  render() {
    return html`
      <div class="wrapper">
        <div
          class="row selected-display"
          @click=${() => {
            this.selectablesVisible = !this.selectablesVisible;
          }}
        >
          <div class="selected-text-display">
            ${this.selected.map((item, index) => {
              if (index === this.selected.length - 1) {
                return html`${item}`;
              }
              return html`${item}, `;
            })}
            ${this.renderNoItems()}
          </div>
          <div class="fill"></div>
          <div class="column down-arrow">${arrowDownIcon()}</div>
        </div>
        <div class="selectables">
          <div class="column">
            ${this.selectable.map(item => {
              if (this.selected.includes(item)) {
                return html`
                  <div
                    class="row selectable-row"
                    @click=${() => {
                      const newArray: Array<string> = [];
                      this.selected.forEach(e => {
                        if (e !== item) {
                          newArray.push(e);
                        }
                      });
                      this.selected = newArray;
                      this.dispatchChangeEvent();
                    }}
                  >
                    ${checkIcon()} ${item}
                  </div>
                `;
              }
              return html`
                <div
                  class="row"
                  @click=${() => {
                    const temp = this.selected;
                    temp.push(item);
                    this.selected = [];
                    this.selected = temp;
                    this.dispatchChangeEvent();
                  }}
                >
                  <div class="empty"></div>
                  ${item}
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    `;
  }

  dispatchChangeEvent = () => {
    const event = new CustomEvent('change', {
      detail: {
        value: JSON.stringify(this.selected),
      },
    });
    this.dispatchEvent(event);
  };

  renderNoItems = () => {
    if (this.selected.length === 0) {
      return html` Nothing selected `;
    }
    return html``;
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'dropdown-multiselect-element': DropdownMultiselectElement;
  }
}
